import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { accountService } from '../services/accountService';
import { expenseService } from '../services/expenseService';
import AccountForm from './AccountForm';
import BucketForm from './BucketForm';
import ConfirmDialog from './ConfirmDialog';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [bucketExpenses, setBucketExpenses] = useState({});
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showBucketForm, setShowBucketForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingBucket, setEditingBucket] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '', accountId: '' });
  const [collapsedAccounts, setCollapsedAccounts] = useState(new Set());
  const [expandedBuckets, setExpandedBuckets] = useState(new Set());
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadAccounts();
  }, []);

  // Load bucket expenses after accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      loadBucketExpenses();
    }
  }, [accounts]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountService.getAccounts();

      // Sort accounts alphabetically by name
      const sortedAccounts = data.sort((a, b) => a.name.localeCompare(b.name));

      // Sort buckets within each account alphabetically by name
      sortedAccounts.forEach(account => {
        if (account.buckets && account.buckets.length > 0) {
          account.buckets.sort((a, b) => a.name.localeCompare(b.name));
        }
      });

      setAccounts(sortedAccounts);

      // Set all accounts as collapsed by default
      setCollapsedAccounts(new Set(sortedAccounts.map(account => account.id)));
    } catch (err) {
      setError('Failed to load accounts');
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBucketExpenses = async () => {
    try {
      setLoadingExpenses(true);
      const grouped = {};

      // Collect all bucket IDs from all accounts
      const allBuckets = accounts.flatMap(account => account.buckets || []);

      // Fetch expenses for each bucket in parallel
      await Promise.all(
        allBuckets.map(async (bucket) => {
          try {
            const data = await expenseService.getExpensesByBucket(bucket.id);
            const dataBody = JSON.parse(data.body);
            const expenses = dataBody.expenses || [];
            if (expenses.length > 0) {
              grouped[bucket.id] = expenses;
            }
          } catch (err) {
            console.error(`Error loading expenses for bucket ${bucket.id}:`, err);
          }
        })
      );

      setBucketExpenses(grouped);
    } catch (err) {
      console.error('Error loading expenses for buckets:', err);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleCreateAccount = () => {
    setEditingAccount(null);
    setShowAccountForm(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setShowAccountForm(true);
  };

  const handleAccountSubmit = async (accountData) => {
    try {
      if (editingAccount) {
        await accountService.updateAccount(editingAccount.id, accountData);
      } else {
        await accountService.createAccount(accountData);
      }
      setShowAccountForm(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (err) {
      setError('Failed to save account');
      console.error('Error saving account:', err);
    }
  };

  const handleDeleteAccount = (accountId) => {
    setDeleteConfirm({
      show: true,
      type: 'account',
      id: accountId,
      accountId: ''
    });
  };

  const handleCreateBucket = (accountId) => {
    setSelectedAccountId(accountId);
    setEditingBucket(null);
    setShowBucketForm(true);
  };

  const handleEditBucket = (accountId, bucket) => {
    setSelectedAccountId(accountId);
    setEditingBucket(bucket);
    setShowBucketForm(true);
  };

  const handleBucketSubmit = async (bucketData) => {
    try {
      if (editingBucket) {
        await accountService.updateBucket(selectedAccountId, editingBucket.id, bucketData);
      } else {
        await accountService.createBucket(selectedAccountId, bucketData);
      }
      setShowBucketForm(false);
      setEditingBucket(null);
      setSelectedAccountId(null);
      loadAccounts();
    } catch (err) {
      setError('Failed to save bucket');
      console.error('Error saving bucket:', err);
    }
  };

  const handleDeleteBucket = (accountId, bucketId) => {
    setDeleteConfirm({
      show: true,
      type: 'bucket',
      id: bucketId,
      accountId: accountId
    });
  };

  const confirmDelete = async () => {
    try {
      if (deleteConfirm.type === 'account') {
        await accountService.deleteAccount(deleteConfirm.id);
      } else {
        await accountService.deleteBucket(deleteConfirm.accountId, deleteConfirm.id);
      }
      setDeleteConfirm({ show: false, type: '', id: '', accountId: '' });
      loadAccounts();
    } catch (err) {
      setError('Failed to delete');
      console.error('Error deleting:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTotalBucketAmount = (buckets) => {
    return buckets?.reduce((sum, bucket) => sum + bucket.amount, 0) || 0;
  };

  const getUnallocatedAmount = (account) => {
    const totalBuckets = getTotalBucketAmount(account.buckets);
    return account.balance - totalBuckets;
  };

  const toggleAccountCollapse = (accountId) => {
    setCollapsedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  const isAccountCollapsed = (accountId) => {
    return collapsedAccounts.has(accountId);
  };

  const handleRemoveBucketAssignment = async (expense) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      await expenseService.updateExpense(expense.id, { bucketId: null }, userData.familyId);
      // Update local state
      setBucketExpenses(prev => {
        const updated = { ...prev };
        const bucketId = expense.bucketId;
        if (updated[bucketId]) {
          updated[bucketId] = updated[bucketId].filter(e => e.id !== expense.id);
          if (updated[bucketId].length === 0) {
            delete updated[bucketId];
          }
        }
        return updated;
      });
    } catch (err) {
      console.error('Error removing bucket assignment:', err);
      setError('Failed to remove bucket assignment');
    }
  };

  const handleClearAllBucketAssignments = async (bucketId) => {
    const expenses = bucketExpenses[bucketId];
    if (!expenses || expenses.length === 0) return;

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      await Promise.all(
        expenses.map(expense => expenseService.updateExpense(expense.id, { bucketId: null }, userData.familyId))
      );
      // Update local state
      setBucketExpenses(prev => {
        const updated = { ...prev };
        delete updated[bucketId];
        return updated;
      });
    } catch (err) {
      console.error('Error clearing bucket assignments:', err);
      setError('Failed to clear bucket assignments');
    }
  };

  if (loading) {
    return (
      <div className="accounts-container">
        <header className="page-header">
          <div className="page-header-content">
            <h1 className="page-title">Bank Accounts</h1>
            <p className="page-subtitle">
              Manage your bank accounts and savings buckets for <span className="family-name">{user?.familyName}</span>
            </p>

            <nav className="page-nav">
              <button onClick={() => navigate('/home')} className="nav-btn">
                📊 Dashboard
              </button>
              <button onClick={() => navigate('/categories')} className="nav-btn">
                📂 Categories
              </button>
              <button onClick={() => navigate('/accounts')} className="nav-btn active">
                🏦 Accounts
              </button>
              <button onClick={() => navigate('/help')} className="nav-btn">
                ❓ Help
              </button>
            </nav>
          </div>
          <button onClick={logout} className="btn logout-btn">
            Logout
          </button>
        </header>

        <div className="card">
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#b0b0b0',
            fontSize: '1.1rem'
          }}>
            Loading accounts...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accounts-container">
      <header className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Bank Accounts</h1>
          <p className="page-subtitle">
            Manage your bank accounts and savings buckets for <span className="family-name">{user?.familyName}</span>
          </p>

          <nav className="page-nav">
            <button onClick={() => navigate('/home')} className="nav-btn">
              📊 Dashboard
            </button>
            <button onClick={() => navigate('/categories')} className="nav-btn">
              📂 Categories
            </button>
            <button onClick={() => navigate('/accounts')} className="nav-btn active">
              🏦 Accounts
            </button>
            <button onClick={() => navigate('/help')} className="nav-btn">
              ❓ Help
            </button>
          </nav>
        </div>
        <button onClick={logout} className="btn logout-btn">
          Logout
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            color: '#f0f0f0',
            margin: '0',
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            Bank Accounts
          </h2>
          <button
            onClick={handleCreateAccount}
            className="btn"
          >
            + Add Account
          </button>
        </div>

        <div className="accounts-grid">
          {accounts.map(account => (
            <div key={account.id} className="account-card">
              <div className="account-header">
                <div className="account-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => toggleAccountCollapse(account.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff6b35',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'transform 0.2s ease'
                      }}
                      title={isAccountCollapsed(account.id) ? 'Expand account' : 'Collapse account'}
                    >
                      {isAccountCollapsed(account.id) ? '▶' : '▼'}
                    </button>
                    <div>
                      <h3>{account.name}</h3>
                      <span className="account-type">{account.accountType}</span>
                    </div>
                  </div>
                </div>
                <div className="account-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEditAccount(account)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteAccount(account.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="account-balance">
                <div className="balance-item">
                  <span className="label">Total Balance:</span>
                  <span className="amount">{formatCurrency(account.balance)}</span>
                </div>
                <div className="balance-item">
                  <span className="label">Allocated:</span>
                  <span className="amount">{formatCurrency(getTotalBucketAmount(account.buckets))}</span>
                </div>
                <div className="balance-item unallocated">
                  <span className="label">Unallocated:</span>
                  <span className="amount">{formatCurrency(getUnallocatedAmount(account))}</span>
                </div>
              </div>

              {!isAccountCollapsed(account.id) && (
                <div className="buckets-section">
                  <div className="buckets-header">
                    <h4>Buckets</h4>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleCreateBucket(account.id)}
                    >
                      Add Bucket
                    </button>
                  </div>

                  {account.buckets && account.buckets.length > 0 ? (
                    <div className="buckets-list">
                      {account.buckets.map(bucket => (
                        <div key={bucket.id} className="bucket-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="bucket-info">
                              <span className="bucket-name">{bucket.name}</span>
                              <span className="bucket-amount">{formatCurrency(bucket.amount)}</span>
                            </div>
                            <div className="bucket-actions">
                              <button
                                className="btn btn-xs btn-secondary"
                                onClick={() => handleEditBucket(account.id, bucket)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-xs btn-danger"
                                onClick={() => handleDeleteBucket(account.id, bucket.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          {/* Loading indicator for bucket expenses */}
                          {loadingExpenses && (
                            <div style={{
                              marginTop: '6px',
                              paddingTop: '6px',
                              borderTop: '1px solid #333',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '11px',
                              color: '#888'
                            }}>
                              <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                              Loading expenses...
                            </div>
                          )}
                          {/* Expenses assigned to this bucket */}
                          {!loadingExpenses && bucketExpenses[bucket.id] && bucketExpenses[bucket.id].length > 0 && (
                            <div style={{
                              marginTop: '6px',
                              paddingTop: '6px',
                              borderTop: '1px solid #333'
                            }}>
                              <button
                                onClick={() => {
                                  setExpandedBuckets(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(bucket.id)) {
                                      newSet.delete(bucket.id);
                                    } else {
                                      newSet.add(bucket.id);
                                    }
                                    return newSet;
                                  });
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px 0',
                                  fontSize: '11px',
                                  color: '#9b59b6',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  width: '100%'
                                }}
                              >
                                <span style={{ transition: 'transform 0.2s', transform: expandedBuckets.has(bucket.id) ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                {bucketExpenses[bucket.id].length} expense{bucketExpenses[bucket.id].length !== 1 ? 's' : ''} assigned
                                <span style={{ marginLeft: 'auto', color: '#b0b0b0' }}>
                                  {formatCurrency(bucketExpenses[bucket.id].reduce((sum, e) => sum + e.amount, 0))}
                                </span>
                              </button>
                              {expandedBuckets.has(bucket.id) && (
                                <div className="bucket-expenses-panel">
                                  <div className="bucket-expenses-header">
                                    <button
                                      onClick={() => handleClearAllBucketAssignments(bucket.id)}
                                      className="btn-clear-all"
                                      title="Remove all expense assignments from this bucket"
                                    >
                                      Clear All
                                    </button>
                                  </div>
                                  {bucketExpenses[bucket.id].map(expense => (
                                    <div key={expense.id} className="bucket-expense-row">
                                      <div className="bucket-expense-info">
                                        <span className="bucket-expense-desc">{expense.description}</span>
                                        <div className="bucket-expense-meta">
                                          <span className="bucket-expense-amount">{formatCurrency(expense.amount)}</span>
                                          <span className="bucket-expense-date">{expense.date}</span>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveBucketAssignment(expense)}
                                        className="btn-remove-expense"
                                        title="Remove bucket assignment"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-buckets">No buckets created yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {accounts.length === 0 && !loading && (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            color: '#888',
            background: 'rgba(255, 107, 53, 0.05)',
            borderRadius: '12px',
            border: '2px dashed #404040'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🏦</div>
            <p style={{ fontSize: '1.2rem', margin: '0' }}>
              No accounts found. Click "Add Account" to create your first one!
            </p>
          </div>
        )}
      </div>

      {showAccountForm && (
        <AccountForm
          account={editingAccount}
          onSubmit={handleAccountSubmit}
          onCancel={() => {
            setShowAccountForm(false);
            setEditingAccount(null);
          }}
        />
      )}

      {showBucketForm && (
        <BucketForm
          bucket={editingBucket}
          onSubmit={handleBucketSubmit}
          onCancel={() => {
            setShowBucketForm(false);
            setEditingBucket(null);
            setSelectedAccountId(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title={`Delete ${deleteConfirm.type}`}
        message={`Are you sure you want to delete this ${deleteConfirm.type}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ show: false, type: '', id: '', accountId: '' })}
      />


    </div>
  );
};

export default Accounts;